import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import {
	EncumbranceActionLabels,
	EncumbranceTypeLabels,
} from '@core/util/encumbrance';
import {
	Encumbrance,
	EncumbranceActionType,
	EncumbranceType,
	MortgagePriority,
} from '@models/encumbrance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import {
	EncumbrancesDispatch,
	MatterEncumbrancesState,
} from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { CreateStatusType } from '@state/utils/models';
import { createId } from '@core/util/file';
import { Observable } from 'rxjs/internal/Observable';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { MatterFileType } from '@models/matter';
import { cloneDeep } from 'lodash';

const EncumbranceActions: RadioGroupItem<EncumbranceActionType>[] = [
	{
		value: EncumbranceActionType.DISCHARGE,
		label: EncumbranceActionLabels[EncumbranceActionType.DISCHARGE],
	},
	// Removed for launch
	// {
	// 	value: EncumbranceActionType.ASSUME,
	// 	label: EncumbranceActionLabels[EncumbranceActionType.ASSUME],
	// }
];

const EncumbranceTypes: DropdownOption<EncumbranceType>[] = [
	{
		name: EncumbranceTypeLabels[EncumbranceType.MORTGAGE],
		value: EncumbranceType.MORTGAGE,
	},
	{
		name: EncumbranceTypeLabels[EncumbranceType.NOTICE_OF_SECURITY_INTEREST],
		value: EncumbranceType.NOTICE_OF_SECURITY_INTEREST,
	},
	{
		name: EncumbranceTypeLabels[EncumbranceType.LIEN],
		value: EncumbranceType.LIEN,
	},
];

export interface CreateEncumbranceDialogData {
	encumbrance: Encumbrance;
	mode: 'edit' | 'new';
}

@UntilDestroy()
@Component({
	selector: 'app-create-encumbrance',
	templateUrl: './create-encumbrance.component.html',
	animations: [openClose()],
	standalone: false,
})
export class CreateEncumbranceComponent implements OnInit {
	public id = createId();
	public mode: 'edit' | 'new' = 'new';
	public submitted = false;
	public _createStatus: CreateStatusType;
	public createStatusTypes = CreateStatusType;
	public encumbranceActionsOptions = cloneDeep(EncumbranceActions);
	public encumbranceTypesOptions = EncumbranceTypes;
	public priorityOptions: RadioGroupItem<number>[] = MortgagePriority;
	public encumbranceTypes = EncumbranceType;
	public encumbranceActions = EncumbranceActionType;
	public priority;

	public selectedEncumbranceTypeText =
		EncumbranceTypeLabels[EncumbranceType.MORTGAGE];
	public selectedEncumbranceType: EncumbranceType = EncumbranceType.MORTGAGE;
	public selectedEncumbranceAction = EncumbranceActionType.DISCHARGE;

	@Select(MatterEncumbrancesState.getCreateStatus)
	createStatus: Observable<CreateStatusType>;
	@SelectSnapshot(AuthState.getLoggedInUser) loggedInUser: AuthUser;
	@SelectSnapshot(SelectedMatter.id) selectedMatterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@Select(MatterEncumbrancesState.getSorted) allEncumbrances: Observable<
		Encumbrance[]
	>;

	constructor(
		public dialogRef: MatDialogRef<CreateEncumbranceComponent>,
		@Inject(MAT_DIALOG_DATA) public data: CreateEncumbranceDialogData,
		private encumbranceDispatch: EncumbrancesDispatch,
		private navService: NavigationService,
	) {}

	ngOnInit(): void {
		if (this.data) {
			this.id = this.data.encumbrance.id;
			this.mode = this.data.mode;
			this.selectedEncumbranceType = this.data.encumbrance.type;
			this.selectedEncumbranceTypeText =
				EncumbranceTypeLabels[this.data.encumbrance.type];
			this.selectedEncumbranceAction = this.data.encumbrance.action;
			this.priority = this.data.encumbrance.priority;
		}
		this.encumbranceDispatch.ResetCreateStatus();
		if (
			this.fileType === MatterFileType.PURCHASE ||
			this.fileType === MatterFileType.SALE
		) {
			this.encumbranceActionsOptions.push({
				value: EncumbranceActionType.BRIDGE,
				label: EncumbranceActionLabels[EncumbranceActionType.BRIDGE],
			});
		} else {
			this.encumbranceActionsOptions.push({
				value: EncumbranceActionType.REMAIN,
				label: EncumbranceActionLabels[EncumbranceActionType.REMAIN],
			});
		}
		this.createStatus
			.pipe(untilDestroyed(this))
			.subscribe((value: CreateStatusType) => {
				this._createStatus = value;
				if (this.submitted && value === CreateStatusType.success) {
					this.navService.goToMatter_Mortgages_Encumbrances_Encumbrance(
						this.selectedMatterId,
						this.id,
					);
					this.dialogRef.close(this.id);
				}
			});

		this.allEncumbrances
			.pipe(untilDestroyed(this))
			.subscribe((encumbrances) => {
				if (encumbrances) {
					const mortgageEncumbrances = encumbrances.filter(
						(x) =>
							x.type === EncumbranceType.MORTGAGE &&
							(x.action === EncumbranceActionType.DISCHARGE ||
								x.action === EncumbranceActionType.REMAIN),
					);

					if (!this.priority) {
						this.priority = mortgageEncumbrances?.length + 1;
					}

					this.priorityOptions = this.priorityOptions.map((option) => {
						option.disabled = false;

						return option;
					});

					mortgageEncumbrances.forEach((encumbrance) => {
						const matchingOption = this.priorityOptions.find(
							(option) =>
								option.value === encumbrance.priority &&
								option.value !== this.priority,
						);
						if (matchingOption) {
							matchingOption.disabled = true;
						}
					});
				}
			});
	}

	create() {
		this.submitted = true;
		if (this.mode === 'new') {
			this.encumbranceDispatch.Create(
				this.id,
				this.selectedEncumbranceType,
				this.selectedEncumbranceAction,
				this.priority,
			);
		} else if (this.mode === 'edit') {
			const newEncumbrance: Partial<Encumbrance> = {
				type: this.selectedEncumbranceType,
				action: this.selectedEncumbranceAction,
				priority: this.priority,
			};
			this.encumbranceDispatch.CreateOrMergeEncumbrance(
				this.id,
				newEncumbrance,
			);
			this.navService.goToMatter_Mortgages_Encumbrances_Encumbrance(
				this.selectedMatterId,
				this.id,
			);
			this.dialogRef.close(this.id);
		}
	}

	cancel() {
		this.dialogRef.close();
	}

	setEncumbranceType($event: DropdownResult<DropdownOption<EncumbranceType>>) {
		this.selectedEncumbranceTypeText =
			EncumbranceTypeLabels[$event.result.value];
		this.selectedEncumbranceType = $event.result.value;
	}
}
